import React, { useEffect, useState } from 'react';
import { apiFetch } from './config';
import Nav from './Nav';
import { Explain } from './Explain';
import { useLensWeights } from './lensWeights';

// Glass-box trust inspector: shows HOW the trust scores are built, not just the number.
// Legibility = legitimacy — a trust system you can't inspect is just a new black box.

function bar(value, { signed }) {
  // value in [-1,1] (signed) or [0,1]. Render a small proportional bar.
  const pct = Math.round(Math.abs(value) * 100);
  const color = signed ? (value >= 0 ? 'var(--wv-pos)' : 'var(--wv-neg)') : 'var(--wv-accent)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span style={{ width: '70px', height: '8px', background: 'var(--wv-border)', borderRadius: '4px', overflow: 'hidden' }}>
        <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: color }} />
      </span>
      <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '12px', color: 'var(--wv-muted)' }}>
        {value >= 0 && signed ? '+' : ''}
        {value.toFixed(2)}
      </span>
    </span>
  );
}

const CLUSTER_COLORS = ['#5b8def', '#e0803b', '#3aa66f', '#9b59b6', '#d6577f', '#7f8c8d'];
const clusterChip = (c) =>
  c == null ? null : (
    <span
      style={{
        fontSize: '11px',
        padding: '1px 7px',
        borderRadius: '10px',
        color: 'var(--wv-bg)',
        background: CLUSTER_COLORS[c % CLUSTER_COLORS.length],
      }}
    >
      {c}
    </span>
  );

function TrustInspect() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // /api/trust/eigentrust became auth-gated 2026-08-08 (its payload is a
  // per-actor social graph with names). The ROUTE stays open so a deep link
  // still resolves — the bound lives in the backend, never a login wall — so
  // this page has to explain itself to a signed-out reader.
  const [needsAuth, setNeedsAuth] = useState(false);
  const { engine: selectedEngine } = useLensWeights();
  // Domain projection (vector-trust phase 5): trust(actor, domain) —
  // truthfinder over one domain's claims, an extra column beside the engines.
  const [domains, setDomains] = useState([]);
  const [domainId, setDomainId] = useState('');

  useEffect(() => {
    apiFetch(`/api/trust/eigentrust${domainId ? `?domain=${domainId}` : ''}`)
      .then((r) => {
        // Read the status BEFORE rejecting: `r.ok ? r.json() : reject(...)`
        // threw it away, which is why a 401 would have read as a server fault.
        if (r.status === 401) {
          setNeedsAuth(true);
          return Promise.reject(new Error('unauthenticated'));
        }
        return r.ok ? r.json() : Promise.reject(new Error('failed'));
      })
      .then(setData)
      .catch((e) => {
        if (e && e.message === 'unauthenticated') return;
        setError('Could not load the trust breakdown.');
      });
  }, [domainId]);

  useEffect(() => {
    apiFetch('/api/trust/domains')
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setDomains(Array.isArray(d) ? d : d.domains || []))
      .catch(() => {});
  }, []);

  // People-layer only: engines score USERS. Lenses (object layer) deliberately
  // do not belong on this page (review finding, 2026-07-03).
  const engines = data?.engines || [];
  const users = data
    ? [...data.users].sort(
        (a, b) => (b.engines?.[selectedEngine] ?? 0) - (a.engines?.[selectedEngine] ?? 0)
      )
    : [];

  return (
    <div>
      <Nav />
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 20px 40px' }}>
        <h1 style={{ marginTop: 0 }}><Explain concept="engine">Trust inspector</Explain></h1>

        {needsAuth && (
          <div style={{ color: 'var(--wv-muted)' }}>
            Log in to see the trust breakdown. It names every actor in the trust
            graph and who trusts whom, so it is not public.
          </div>
        )}
        {error && !needsAuth && <div style={{ color: 'var(--wv-neg)' }}>{error}</div>}
        {!data && !error && !needsAuth && <div style={{ color: 'var(--wv-muted)' }}>Computing…</div>}

        {data && (
          <>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '20px',
                padding: '12px 16px',
                background: 'var(--wv-surface2)',
                border: '1px solid var(--wv-border)',
                borderRadius: '6px',
                marginBottom: '24px',
                fontSize: '13px',
              }}
            >
              <div>
                <div style={{ color: 'var(--wv-muted)' }}><Explain concept="trust-anchor">Anchor</Explain></div>
                <strong>
                  {data.anchorIsPersonalized
                    ? `Personalized (you: ${data.viewerName || 'you'})`
                    : 'Global / uniform (cold-start)'}
                </strong>
              </div>
              <div>
                <div style={{ color: 'var(--wv-muted)' }}><Explain concept="convergence">Convergence</Explain></div>
                <strong>
                  {data.converged ? `converged in ${data.iterations} iters` : `stopped at ${data.iterations}`}
                </strong>
              </div>
              <div>
                <div style={{ color: 'var(--wv-muted)' }}><Explain concept="teleport-alpha">Teleport α</Explain></div>
                <strong>{data.alpha}</strong>
              </div>
              <div>
                <div style={{ color: 'var(--wv-muted)' }}>Participants</div>
                <strong>{data.users.length}</strong>
              </div>
            </div>

            {!data.anchorIsPersonalized && (
              <p style={{ fontSize: '13px', color: 'var(--wv-warn)', background: 'color-mix(in srgb, var(--wv-warn) 10%, transparent)', padding: '8px 12px', borderRadius: '4px' }}>
                You haven't expressed any trust yet (or aren't logged in), so this is the global
                fallback — everyone is an equal anchor. Trust someone and it personalizes to your network.
              </p>
            )}

            <h3>Scores by participant</h3>
            <p style={{ fontSize: '12px', color: 'var(--wv-muted)', marginTop: '-6px' }}>
              Sorted by <strong>{engines.find((e) => e.id === selectedEngine)?.label || selectedEngine}</strong>{' '}
              (your viewpoint drawer selection).
            </p>
            {domains.length > 0 && (
              <p style={{ fontSize: '12px', marginTop: '2px' }}>
                <Explain
                  title="Domain track record"
                  text="trust(actor, domain): the TruthFinder content engine run over only the claims tagged into one domain (subcategories roll up). The same actor can be reliable in nutrition and unreliable in economics — this column is where that stops being invisible. A read-time projection of existing votes; nothing new is stored."
                >
                  <span className="trust-muted">domain track record:</span>
                </Explain>{' '}
                <select value={domainId} onChange={(e) => setDomainId(e.target.value)} style={{ fontSize: '12px' }}>
                  <option value="">— none —</option>
                  {domains.map((d) => (
                    <option key={d.tag_id} value={d.tag_id}>{d.name}</option>
                  ))}
                </select>
                {data.domain && (
                  <span className="trust-muted" style={{ marginLeft: '8px' }}>
                    {data.domain.member_count} claims in {data.domain.name}
                  </span>
                )}
              </p>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--wv-text)' }}>
                    <th style={{ padding: '6px 8px' }}>User</th>
                    <th style={{ padding: '6px 8px' }}><Explain concept="trust-cluster">Cluster</Explain></th>
                    {engines.map((e) => (
                      <th
                        key={e.id}
                        title={e.description}
                        style={{
                          padding: '6px 8px',
                          background: e.id === selectedEngine ? 'color-mix(in srgb, var(--wv-accent) 16%, transparent)' : 'transparent',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Explain title={`${e.label} engine`} text={e.description} graphKey={`engine:${e.id}`}>
                          {e.label}
                        </Explain>
                        {e.status === 'diagnostic' && (
                          <Explain concept="diagnostic-engine">
                            <span style={{ fontSize: '10px', color: 'var(--wv-warn)', display: 'block' }}>diagnostic flag</span>
                          </Explain>
                        )}
                      </th>
                    ))}
                    {data.domain && (
                      <th style={{ padding: '6px 8px', whiteSpace: 'nowrap', background: 'color-mix(in srgb, var(--wv-pos) 12%, transparent)' }}>
                        <Explain
                          title={`Track record in ${data.domain.name}`}
                          text="TruthFinder over only this domain's claims: does this actor's record hold up HERE. Compare against the global truthfinder column — divergence between them is domain expertise (or domain unreliability) the global score averages away."
                        >
                          in {data.domain.name}
                        </Explain>
                      </th>
                    )}
                    <th style={{ padding: '6px 8px' }}>
                      <Explain
                        title="Bridge"
                        text="The anti-bubble counterweight: high when someone is trusted across otherwise-separated groups (removing them would split those groups), zero when their trust comes from a single bubble. A perspective to compare, never a single verdict."
                      >
                        Bridge
                      </Explain>
                    </th>
                    <th style={{ padding: '6px 8px' }}>
                      <Explain
                        title="Raw trust / distrust"
                        text="The unnormalized trust and distrust mass reaching this account, before any engine's scaling — shown so the engine columns can be checked against their inputs."
                      >
                        raw trust
                      </Explain>
                    </th>
                    <th style={{ padding: '6px 8px' }}>distrust</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} style={{ borderBottom: '1px solid var(--wv-border)' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 'bold' }}>{u.username}</td>
                      <td style={{ padding: '6px 8px' }}>{clusterChip(u.cluster)}</td>
                      {engines.map((e) => {
                        const s = u.engines?.[e.id] ?? 0;
                        return (
                          <td key={e.id} style={{ padding: '6px 8px', background: e.id === selectedEngine ? 'color-mix(in srgb, var(--wv-accent) 16%, transparent)' : 'transparent' }}>
                            {e.id === selectedEngine ? (
                              bar(s, { signed: true })
                            ) : (
                              <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '12px', color: s === 0 ? 'var(--wv-quiet)' : 'var(--wv-muted)' }}>
                                {s === 0 ? '—' : (s > 0 ? '+' : '') + s.toFixed(2)}
                              </span>
                            )}
                          </td>
                        );
                      })}
                      {data.domain && (() => {
                        const s = data.domain.scores[u.id] ?? 0;
                        return (
                          <td style={{ padding: '6px 8px', background: 'color-mix(in srgb, var(--wv-pos) 8%, transparent)' }}>
                            <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '12px', color: s === 0 ? 'var(--wv-quiet)' : 'var(--wv-muted)' }}>
                              {s === 0 ? '—' : (s > 0 ? '+' : '') + s.toFixed(2)}
                            </span>
                          </td>
                        );
                      })()}
                      <td style={{ padding: '6px 8px' }}>
                        {u.bridge > 0.01 ? bar(u.bridge, { signed: false }) : <span style={{ color: 'var(--wv-quiet)' }}>—</span>}
                      </td>
                      <td style={{ padding: '6px 8px', color: 'var(--wv-muted)', fontVariantNumeric: 'tabular-nums' }}>
                        {u.trust.toFixed(3)}
                      </td>
                      <td style={{ padding: '6px 8px', color: u.distrust > 0 ? 'var(--wv-neg)' : 'var(--wv-quiet)', fontVariantNumeric: 'tabular-nums' }}>
                        {u.distrust > 0 ? u.distrust.toFixed(3) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 style={{ marginTop: '28px' }}>
              <Explain concept="activation">Trust edges feeding the computation</Explain>
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {data.edges.map((e, i) => (
                <div key={i} style={{ fontSize: '13px' }}>
                  <strong>{e.from}</strong>{' '}
                  <span style={{ color: e.polarity < 0 ? 'var(--wv-neg)' : 'var(--wv-pos)' }}>
                    {e.polarity < 0 ? 'distrusts' : 'trusts'}
                  </span>{' '}
                  <strong>{e.to}</strong>
                </div>
              ))}
            </div>

          </>
        )}
      </div>
    </div>
  );
}

export default TrustInspect;
